// DEV-11「拉日誌（需 owner 授權遠端診斷）」：客服請求對這台設備做唯讀遠端偵測（kinio_system.diag_readonly）時，
// 成員在這裡看得到請求；owner / admin 同意（多久）、拒絕、撤回。原始日誌不給使用者下載（規格：debug log 只開給平台維運）。
// API 是 App 同一組：GET /v1/homes/{id}/diag-grants、POST …/{grant}/approve {minutes} | deny、DELETE …/{grant}。
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { KinioError, kinioFetch, planeBase } from '@kinio/web-sdk';
import type { VmsDevice } from '../../lib/devices';
import { formatDateTime } from '../../lib/tz';
import { useTzResolver } from '../../lib/useTz';

export interface DiagGrant {
  grant_id: string;
  device_ref: string;
  brand: string;
  state: 'requested' | 'granted' | 'denied' | 'revoked' | 'expired';
  minutes: number;
  requested_at: string;
  expires_at?: string;
  runs?: unknown[];
}

const base = (api: string, homeId: string) => `${planeBase(api)}/v1/homes/${encodeURIComponent(homeId)}/diag-grants`;

export const diagApi = {
  list: (api: string, homeId: string) => kinioFetch<{ grants: DiagGrant[] }>(base(api, homeId)).then((r) => r?.grants ?? []),
  approve: (api: string, homeId: string, id: string, minutes: number) =>
    kinioFetch<DiagGrant>(`${base(api, homeId)}/${encodeURIComponent(id)}/approve`, { method: 'POST', body: { minutes } }),
  deny: (api: string, homeId: string, id: string) => kinioFetch<DiagGrant>(`${base(api, homeId)}/${encodeURIComponent(id)}/deny`, { method: 'POST' }),
  revoke: (api: string, homeId: string, id: string) => kinioFetch<DiagGrant>(`${base(api, homeId)}/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};

/** 這台設備（通道算在 parent 上）的授權，進行中的在前。 */
export function grantsOf(all: DiagGrant[], dev: Pick<VmsDevice, 'aid' | 'parentAid'>): DiagGrant[] {
  const aid = dev.parentAid ?? dev.aid;
  const rank = (g: DiagGrant) => (g.state === 'requested' ? 0 : g.state === 'granted' ? 1 : 2);
  return all.filter((g) => g.device_ref === aid || g.device_ref === dev.aid).sort((a, b) => rank(a) - rank(b) || b.requested_at.localeCompare(a.requested_at));
}

export function DiagGrants({ dev, canDecide }: { dev: VmsDevice; canDecide: boolean }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const tzOf = useTzResolver();
  const qk = ['diag-grants', dev.homeId];
  const q = useQuery({ queryKey: qk, queryFn: () => diagApi.list(dev.apiEndpoint, dev.homeId), refetchInterval: 15_000, retry: false });
  const [minutes, setMinutes] = useState(30);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const list = grantsOf(q.data ?? [], dev);
  const run = async (id: string, fn: () => Promise<unknown>) => {
    setBusy(id);
    setErr(null);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: qk });
    } catch (e) {
      setErr(e instanceof KinioError ? e.code : String(e));
    } finally {
      setBusy(null);
    }
  };
  const when = (s?: string) => (s ? formatDateTime(Date.parse(s) / 1000, tzOf(dev.homeId), i18n.language) : '—');
  return (
    <section className="card" aria-label={t('diag.title')} data-testid="diag-grants">
      <h2 className="section-title">{t('diag.title')}</h2>
      <p className="muted small">{t('diag.help')}</p>
      {err && <p className="error small">{err}</p>}
      {list.length === 0 ? (
        <p className="muted">{t('diag.none')}</p>
      ) : (
        <ul className="diag-list">
          {list.map((g) => (
            <li key={g.grant_id} data-grant={g.grant_id} data-state={g.state}>
              <span className={`badge diag-${g.state}`}>{t(`diag.st_${g.state}`)}</span>
              <span>
                {t('diag.by_brand', { brand: g.brand })} · {when(g.requested_at)}
              </span>
              {g.state === 'granted' && <span className="muted small">{t('diag.until', { at: when(g.expires_at) })}</span>}
              {(g.runs?.length ?? 0) > 0 && <span className="muted small">{t('diag.runs', { n: g.runs!.length })}</span>}
              <span className="spacer" />
              {canDecide && g.state === 'requested' && (
                <>
                  <select className="input" value={minutes} onChange={(e) => setMinutes(Number(e.target.value))} aria-label={t('diag.minutes')}>
                    {[15, 30, 60].map((m) => (
                      <option key={m} value={m}>
                        {t('exports.n_min', { n: m })}
                      </option>
                    ))}
                  </select>
                  <button
                    className="btn btn-sm btn-primary"
                    disabled={busy === g.grant_id}
                    onClick={() => void run(g.grant_id, () => diagApi.approve(dev.apiEndpoint, dev.homeId, g.grant_id, minutes))}
                  >
                    {t('diag.approve')}
                  </button>
                  <button
                    className="btn btn-sm"
                    disabled={busy === g.grant_id}
                    onClick={() => void run(g.grant_id, () => diagApi.deny(dev.apiEndpoint, dev.homeId, g.grant_id))}
                  >
                    {t('diag.deny')}
                  </button>
                </>
              )}
              {canDecide && g.state === 'granted' && (
                <button
                  className="btn btn-sm"
                  disabled={busy === g.grant_id}
                  onClick={() => void run(g.grant_id, () => diagApi.revoke(dev.apiEndpoint, dev.homeId, g.grant_id))}
                >
                  {t('diag.revoke')}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
